import { useTranslation } from 'react-i18next';
import { SubPartnerAccount, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { TransactionHistory } from 'shared/transactionHistory';
import PartnerScopedApiProvider from '@/components/Partners/PartnerManagement/PartnerScopedApiProvider';
import { canReadPartnerTransactions } from '@/components/Partners/PartnerManagement/partnerSelfScopedReads';
import usePartnerTransactionsHostProps from './usePartnerTransactionsHostProps';
import styles from './PartnerTransactionsTab.module.scss';

const cn = withStyles(styles);

type PartnerTransactionsTabProps = {
  partner: SubPartnerAccount;
};

/**
 * The partner page's Account Transactions tab (spec 5.6.6, ID-13796): the shared Transaction History,
 * served by the partner route with the partner account's own bearer, listing the transactions of every
 * account it created. Chrome-less by design — the tab card is the container, and the heading rides on
 * the list's own control row. The route only answers an Active partner account, so one still onboarding
 * reads as not yet available rather than as an error.
 */
export default function PartnerTransactionsTab({ partner }: PartnerTransactionsTabProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerTransactionsTab' });
  const hostProps = usePartnerTransactionsHostProps(partner);

  if (!canReadPartnerTransactions(partner)) {
    return (
      <BodyText className={cn('message')} data-testid="partnerTransactionsInactive">
        {t('inactive', { defaultValue: 'Account transactions become available once the partner account is Active.' })}
      </BodyText>
    );
  }

  // A row the list returned without a token cannot sign the read: nothing the list rendered would
  // belong to this partner account, so the tab says so instead.
  if (!partner.Token) {
    return (
      <BodyText className={cn('message')} data-testid="partnerTransactionsUnavailable">
        {t('unavailable', { defaultValue: "This partner account's transactions could not be loaded." })}
      </BodyText>
    );
  }

  return (
    // The list's table is wider than the tab card; it scrolls sideways inside the tab instead of
    // spilling past the card's edge.
    <div className={cn('scroll')}>
      <PartnerScopedApiProvider bearer={partner.Token}>
        <TransactionHistory {...hostProps} title={t('title', { defaultValue: 'Account Transactions' })} />
      </PartnerScopedApiProvider>
    </div>
  );
}
